/* ── Un-Map: attack-origins visualisation ──────────────────────────────
   Loaded deferred alongside d3 + topojson (both also deferred, earlier in
   source order, so they're executed before this runs). Data-file URLs are
   read from data-* attributes on .unmap-wrap (this file has no PHP context). */

// ── Tree-shaken d3: import only the submodule functions this map uses,
//    then reassemble a small `d3` namespace so call sites stay `d3.foo()`. ──
import { format } from "d3-format";
import { sum, min, max, mean, rollup } from "d3-array";
import { select } from "d3-selection";
import { geoNaturalEarth1, geoPath, geoGraticule } from "d3-geo";
import { zoom } from "d3-zoom";
import { scaleSqrt, scaleSequentialLog } from "d3-scale";
import { interpolate } from "d3-interpolate";
import { feature } from "topojson-client";

const d3 = {
  format, sum, min, max, mean, rollup,
  select, geoNaturalEarth1, geoPath, geoGraticule,
  zoom, scaleSqrt, scaleSequentialLog, interpolate,
};
const topojson = { feature };

(async function () {
  const loadingEl = document.getElementById('unmap-loading');
  const wrap      = document.querySelector('.unmap-wrap');

  // ── Load data from external JSON files (URLs injected via data-* attrs) ──
  let ATTACK_DATA, WORLD_DATA;
  try {
    const fetchJSON = async (url) => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText} — ${url}`);
      return r.json();
    };
    [ATTACK_DATA, WORLD_DATA] = await Promise.all([
      fetchJSON(wrap.dataset.geo),
      fetchJSON(wrap.dataset.world),
    ]);
  } catch (err) {
    console.error('Un-Map: failed to load data —', err);
    loadingEl.textContent = 'MAP DATA FAILED TO LOAD';
    return;
  }

  loadingEl.remove();

  // ── Partition: cloud vs confirmed attackers ──
  const data      = ATTACK_DATA.filter(d => d.lat != null && d.lon != null);
  const cloudIPs  = ATTACK_DATA.filter(d => d.cloud_provider);
  const attackIPs = data.filter(d => !d.cloud_provider);
  const cloudPlottable = data.filter(d => d.cloud_provider);

  // ── Header stats ──
  document.getElementById('stat-ips').textContent       = d3.format(',')(ATTACK_DATA.length);
  document.getElementById('stat-hits').textContent      = d3.format('.2s')(d3.sum(ATTACK_DATA, d => d.hits));
  document.getElementById('stat-countries').textContent = new Set(ATTACK_DATA.map(d => d.country_code).filter(Boolean)).size;
  document.getElementById('stat-cloud').textContent     = cloudIPs.length;
  document.getElementById('stat-threshold').textContent = (ATTACK_DATA[0]?.days ?? '?') + 'd+';
  document.getElementById('unmap-report-label').textContent = ATTACK_DATA[0]?.source_report ?? '';

  // ── Top countries sidebar ──
  {
    const byCountry = d3.rollup(
      ATTACK_DATA.filter(d => d.country),
      v => ({ count: v.length, hits: d3.sum(v, d => d.hits) }),
      d => d.country
    );
    const sorted = Array.from(byCountry.entries())
      .sort((a, b) => b[1].hits - a[1].hits)
      .slice(0, 20);
    const maxHits = sorted[0]?.[1].hits ?? 1;
    const list = document.getElementById('country-list');
    sorted.forEach(([country, {count, hits}]) => {
      const pct = Math.round((hits / maxHits) * 100);
      list.insertAdjacentHTML('beforeend', `
        <div class="country-row">
          <span class="country-name">${country}</span>
          <span class="country-count">${d3.format(',')(hits)}</span>
        </div>
        <div class="country-bar" style="width:${pct}%"></div>
      `);
    });
  }

  // ── Dimensions ──
  const statsH = document.getElementById('unmap-stats').offsetHeight;
  const W = window.innerWidth;
  const H = window.innerHeight - statsH;
  document.getElementById('unmap-map').style.height = H + 'px';

  const svg = d3.select('#unmap-map').append('svg').attr('width', W).attr('height', H);

  // ── Projection ──
  const projection = d3.geoNaturalEarth1()
    .scale(W / 6.4)
    .translate([W / 2, H / 2]);
  const pathGen = d3.geoPath().projection(projection);

  // ── Zoom ──
  const g = svg.append('g');
  svg.call(d3.zoom().scaleExtent([1, 24]).on('zoom', e => g.attr('transform', e.transform)));

  // ── Draw world ──
  g.append('path').datum({ type: 'Sphere' }).attr('class', 'sphere').attr('d', pathGen);
  g.append('path').datum(d3.geoGraticule()()).attr('class', 'graticule').attr('d', pathGen);
  const countries = topojson.feature(WORLD_DATA, WORLD_DATA.objects.countries);
  g.selectAll('.country').data(countries.features).join('path').attr('class', 'country').attr('d', pathGen);

  // ── Scales ──
  const maxHits = d3.max(data, d => d.hits);
  const minHits = d3.min(data, d => d.hits);
  const rScale = d3.scaleSqrt().domain([minHits, maxHits]).range([3, 32]);
  const colorScale = d3.scaleSequentialLog()
    .domain([Math.max(1, minHits), maxHits])
    .interpolator(d3.interpolate('#ff3a00', '#ffe066'));

  // ── Tooltip ──
  const tooltip = document.getElementById('unmap-tooltip');

  function fmt(n) { return d3.format(',')(n); }

  function showTip(event, d) {
    const loc = [d.city, d.region, d.country].filter(Boolean).join(', ') || 'Unknown';
    const sshPct = d.hits > 0 ? Math.round((d.ssh_hits / d.hits) * 100) : 0;
    const cloudWarning = d.cloud_provider
      ? `<div class="tip-cloud-warning">&#9888; ${d.cloud_provider} — review before banning</div>`
      : '';
    tooltip.className = d.cloud_provider ? 'is-cloud' : '';
    tooltip.innerHTML = `
      <div class="tip-ip">${d.ip}</div>
      ${cloudWarning}
      <div class="tip-location">${loc}</div>
      <table>
        <tr><td>Total hits</td><td>${fmt(d.hits)}</td></tr>
        <tr><td>SSH hits</td><td>${fmt(d.ssh_hits)} (${sshPct}%)</td></tr>
        <tr><td>UFW blocks</td><td>${fmt(d.ufw_hits)}</td></tr>
        <tr><td>Days active</td><td>${d.days} / 7</td></tr>
        <tr><td>ISP</td><td>${d.isp ?? '—'}</td></tr>
        <tr><td>ASN</td><td>${d.asn ?? '—'}</td></tr>
      </table>
      <div class="tip-servers">Servers: ${(d.servers ?? []).join(' · ')}</div>
    `;
    tooltip.style.display = 'block';
    moveTip(event);
  }

  function moveTip(event) {
    const margin = 14;
    const tw = 300, th = 180;
    let x = event.clientX + margin;
    let y = event.clientY + margin;
    if (x + tw > W) x = event.clientX - tw - margin;
    if (y + th > window.innerHeight) y = event.clientY - th - margin;
    tooltip.style.left = x + 'px';
    tooltip.style.top  = y + 'px';
  }

  // ── Draw attacker circles (orange/yellow) ──
  g.selectAll('.attack-dot')
    .data(attackIPs)
    .join('circle')
    .attr('class', 'attack-dot')
    .attr('cx', d => projection([d.lon, d.lat])[0])
    .attr('cy', d => projection([d.lon, d.lat])[1])
    .attr('r',  d => rScale(d.hits))
    .attr('fill',         d => colorScale(d.hits))
    .attr('opacity', 0.60)
    .attr('stroke',       d => colorScale(d.hits))
    .attr('stroke-width', 0.8)
    .on('mouseenter', (event, d) => { d3.select(event.target).attr('opacity', 1);    showTip(event, d); })
    .on('mousemove',  moveTip)
    .on('mouseleave', (event) => { d3.select(event.target).attr('opacity', 0.60); tooltip.style.display = 'none'; });

  // ── Draw cloud provider circles (cyan dashed) ──
  g.selectAll('.cloud-dot')
    .data(cloudPlottable)
    .join('circle')
    .attr('class', 'cloud-dot')
    .attr('cx', d => projection([d.lon, d.lat])[0])
    .attr('cy', d => projection([d.lon, d.lat])[1])
    .attr('r',  d => rScale(d.hits))
    .attr('fill',         'rgba(0,188,212,0.25)')
    .attr('opacity', 0.75)
    .attr('stroke',       '#00e5ff')
    .attr('stroke-width', 1.5)
    .on('mouseenter', (event, d) => { d3.select(event.target).attr('opacity', 1);    showTip(event, d); })
    .on('mousemove',  moveTip)
    .on('mouseleave', (event) => { d3.select(event.target).attr('opacity', 0.75); tooltip.style.display = 'none'; });

  // ── Legend ──
  {
    const legendEl = document.getElementById('unmap-legend');
    const tiers = [
      { label: 'Low',     hits: minHits },
      { label: 'Medium',  hits: Math.round(maxHits * 0.1) },
      { label: 'High',    hits: Math.round(maxHits * 0.4) },
      { label: 'Extreme', hits: maxHits },
    ];
    tiers.forEach(t => {
      const r = Math.round(rScale(t.hits));
      const col = colorScale(t.hits);
      legendEl.insertAdjacentHTML('beforeend', `
        <div class="legend-row">
          <div class="legend-dot" style="width:${r*2}px;height:${r*2}px;background:${col};opacity:0.7;"></div>
          <span style="color:#8ab0d0">${t.label}</span>
          <span style="color:#4a6080;margin-left:auto">${d3.format('.2s')(t.hits)}</span>
        </div>
      `);
    });
    const cr = Math.round(rScale(d3.mean(data, d => d.hits) || minHits));
    legendEl.insertAdjacentHTML('beforeend', `
      <div class="legend-row" style="margin-top:8px;padding-top:6px;border-top:1px solid #0e2040">
        <div class="legend-cloud-dot" style="width:${cr*2}px;height:${cr*2}px;"></div>
        <span style="color:#00bcd4">Cloud IP &#9888;</span>
      </div>
    `);
  }
})();
